import { initializeApp, type FirebaseApp } from "firebase/app";
import {
    getAuth,
    createUserWithEmailAndPassword,
    signInWithEmailAndPassword,
    signOut,
} from "firebase/auth";
import {
    initializeAnalytics,
    isSupported,
    type Analytics,
} from "firebase/analytics";

const firebaseConfig = {
    apiKey: process.env.REACT_APP_FIREBASE_API_KEY || '',
    authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN || '',
    projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID || '',
    storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET || '',
    messagingSenderId: process.env.REACT_APP_FIREBASE_PROJECT_NUMBER || '',
    appId: process.env.REACT_APP_FIREBASE_APP_ID || ''
};

function isGaDebugEnabled(): boolean {
    if (typeof window === "undefined") {
        return false;
    }
    try {
        return new URLSearchParams(window.location.search).get("ga_debug") === "1";
    } catch {
        return false;
    }
}

// Only initialize Firebase if API key is provided
let app: FirebaseApp | null = null;
let auth: any = null;
let analytics: Analytics | null = null;
const analyticsReadyWaiters: Array<(instance: Analytics) => void> = [];

if (firebaseConfig.apiKey && firebaseConfig.projectId) {
    try {
        app = initializeApp(firebaseConfig);
        auth = getAuth(app);
        void isSupported()
            .then((supported) => {
                if (!supported || !app) {
                    return;
                }
                analytics = initializeAnalytics(app, {
                    config: {
                        send_page_view: false,
                        ...(isGaDebugEnabled() ? { debug_mode: true } : {}),
                    },
                });
                analyticsReadyWaiters.splice(0).forEach((waiter) => waiter(analytics as Analytics));
            })
            .catch((error) => {
                console.warn("Firebase Analytics initialization failed:", error);
            });
    } catch (error) {
        console.warn('Firebase initialization failed:', error);
    }
} else {
    console.warn('Firebase not initialized: Missing API key or project ID');
}

export function getFirebaseAnalytics(onReady: (instance: Analytics) => void) {
    if (analytics) {
        onReady(analytics);
        return;
    }
    analyticsReadyWaiters.push(onReady);
}

export { auth, createUserWithEmailAndPassword, signInWithEmailAndPassword };

export const registerUser = async (email: string, password: string) => {
    if (!auth) throw new Error('Firebase not initialized');
    const userCredential = await createUserWithEmailAndPassword(auth, email, password);
    return userCredential.user;
};

export const loginUser = async (email: string, password: string) => {
    if (!auth) throw new Error('Firebase not initialized');
    const userCredential = await signInWithEmailAndPassword(auth, email, password);
    return userCredential.user;
};

export const logoutUser = async () => {
    if (!auth) throw new Error('Firebase not initialized');
    await signOut(auth);
};
